import { FormEvent, useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Mail, Smartphone, Loader2, ShieldCheck } from 'lucide-react'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import AuthLayout from '@/components/AuthLayout'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface TwoFactorVerifyProps {
  otpMethod: 'email' | 'app'
  verifyUrl: string
  resendUrl: string | null
  locale: string
  logoUrl: string
  translations: {
    verificationTitle: string
    enterCode: string
    enterEmailCode: string
    verify: string
    resendCode: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function TwoFactorVerify({
  otpMethod,
  verifyUrl,
  resendUrl,
  locale,
  logoUrl,
  translations: t,
}: TwoFactorVerifyProps) {
  const [otp, setOtp] = useState('')
  const [processing, setProcessing] = useState(false)
  const [resending, setResending] = useState(false)

  const isEmail = otpMethod === 'email'

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    router.post(
      verifyUrl,
      { otp_attempt: otp },
      { onFinish: () => setProcessing(false) },
    )
  }

  const handleResend = () => {
    if (!resendUrl) return
    setResending(true)
    router.post(resendUrl, {}, { onFinish: () => setResending(false) })
  }

  return (
    <>
      <Head title={t.verificationTitle} />

      <AuthLayout locale={locale} logoUrl={logoUrl}>
        <div className="rounded-2xl bg-white shadow-xl shadow-slate-200/60 border border-slate-100 p-8 sm:p-10">
          <div className="text-center mb-8">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2DD4BF]/10 to-[#8B5CF6]/10">
              {isEmail ? (
                <Mail className="h-7 w-7 text-[#8B5CF6]" />
              ) : (
                <Smartphone className="h-7 w-7 text-[#2DD4BF]" />
              )}
            </div>
            <h1 className="text-2xl font-semibold text-[#252854] mb-1">{t.verificationTitle}</h1>
            <p className="text-sm text-slate-400">
              {isEmail ? t.enterEmailCode : t.enterCode}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={setOtp}
                autoFocus
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <Button
              type="submit"
              disabled={processing || otp.length < 6}
              className="w-full h-11 rounded-xl text-sm font-medium bg-gradient-to-r from-[#2DD4BF] to-[#8B5CF6] hover:from-[#14B8A6] hover:to-[#7C3AED] text-white shadow-lg shadow-[#8B5CF6]/20 transition-all duration-200"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  {t.verify}
                </span>
              )}
            </Button>
          </form>

          {isEmail && resendUrl && (
            <div className="mt-5 text-center">
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="text-sm font-medium text-[#8B5CF6] hover:text-[#7C3AED] transition-colors disabled:opacity-50"
              >
                {resending ? (
                  <Loader2 className="inline h-3.5 w-3.5 animate-spin mr-1.5" />
                ) : null}
                {t.resendCode}
              </button>
            </div>
          )}
        </div>
      </AuthLayout>
    </>
  )
}
